import {
  APInvoiceStatus,
  OriginSyncStatus,
  PaymentStatus,
} from '@erp/shared-types';

export const IGNORABLE_STATUSES = [
  APInvoiceStatus.DRAFT,
  APInvoiceStatus.MATCHED,
  APInvoiceStatus.ACCEPTED,
] as const;

const IGNORABLE_STATUS_SET = new Set<string>(IGNORABLE_STATUSES);

const RECORDED_PAYMENT_STATUSES = new Set<string>([
  PaymentStatus.PAID,
  PaymentStatus.PARTIALLY_PAID,
  PaymentStatus.OVERPAID,
]);

export function isIgnorableInvoiceStatus(status: string): boolean {
  return IGNORABLE_STATUS_SET.has(status);
}

export function isUnpaidFinalizedInvoice(
  status: string,
  paymentStatus?: string | null,
): boolean {
  return (
    status === APInvoiceStatus.FINALIZED &&
    (paymentStatus == null || !RECORDED_PAYMENT_STATUSES.has(paymentStatus))
  );
}

export function isSelectedIgnoreCandidate(invoice: {
  status: string;
  paymentStatus?: string | null;
}): boolean {
  return (
    isIgnorableInvoiceStatus(invoice.status) ||
    isUnpaidFinalizedInvoice(invoice.status, invoice.paymentStatus)
  );
}

export function isIgnoredInvoiceStatus(status: string): boolean {
  return status === APInvoiceStatus.IGNORED;
}

export type SingleInvoiceIgnoreDecision = {
  canIgnore: boolean;
  /** Unpaid finalized invoices are ignored only when the caller opts in. */
  includeFinalizedUnpaid: boolean;
};

const HIDDEN_IGNORE: SingleInvoiceIgnoreDecision = {
  canIgnore: false,
  includeFinalizedUnpaid: false,
};

function isSyncedToFamous(invoice: {
  originSyncStatus?: string | null;
  originSystem?: string | null;
}): boolean {
  return (
    invoice.originSyncStatus === OriginSyncStatus.Success &&
    invoice.originSystem === 'famous'
  );
}

/**
 * Whether one open invoice can be ignored from its page.
 * Mirrors backend `getIgnoreInvoiceBlockReason` order: recorded payments,
 * void, and Famous-synced working invoices stay blocked. Unpaid finalized
 * invoices are allowed only with the opt-in flag, including Famous-synced ones.
 */
export function singleInvoiceIgnoreDecision(invoice: {
  status: string;
  paymentStatus?: string | null;
  originSyncStatus?: string | null;
  originSystem?: string | null;
}): SingleInvoiceIgnoreDecision {
  if (
    invoice.status === APInvoiceStatus.IGNORED ||
    invoice.status === APInvoiceStatus.VOID
  ) {
    return HIDDEN_IGNORE;
  }
  if (
    invoice.paymentStatus != null &&
    RECORDED_PAYMENT_STATUSES.has(invoice.paymentStatus)
  ) {
    return HIDDEN_IGNORE;
  }
  if (isUnpaidFinalizedInvoice(invoice.status, invoice.paymentStatus)) {
    return { canIgnore: true, includeFinalizedUnpaid: true };
  }
  if (
    invoice.status === APInvoiceStatus.FINALIZED ||
    isSyncedToFamous(invoice)
  ) {
    return HIDDEN_IGNORE;
  }
  if (!isIgnorableInvoiceStatus(invoice.status)) return HIDDEN_IGNORE;
  return { canIgnore: true, includeFinalizedUnpaid: false };
}

export function selectedIgnoreInvoices<T extends { status: string }>(
  invoices: T[],
  includeFinalizedUnpaid: boolean,
): T[] {
  if (includeFinalizedUnpaid) return invoices;
  return invoices.filter((invoice) => isIgnorableInvoiceStatus(invoice.status));
}

export function buildBulkIgnorePayload(params: {
  invoices: { id: string; status: string }[];
  includeFinalizedUnpaid: boolean;
}): {
  invoiceIds: string[];
  includeFinalizedUnpaid: boolean;
} {
  return {
    invoiceIds: selectedIgnoreInvoices(
      params.invoices,
      params.includeFinalizedUnpaid,
    ).map((invoice) => invoice.id),
    includeFinalizedUnpaid: params.includeFinalizedUnpaid,
  };
}
